// src/widgets/PdpaWidget.jsx
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Shield } from 'lucide-react'

const pdpaContent = (
  <div className="space-y-6 text-sm leading-6 text-gray-800">
    <div className="space-y-1">
      <h3 className="text-xl font-semibold text-gray-900">นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA) และแบบคำยินยอม</h3>
      <div className="text-xs text-gray-500">มีผลบังคับใช้: 17 กันยายน 2025</div>
      <p>เอกสารนี้อธิบายการเก็บ ใช้ เปิดเผย และคุ้มครองข้อมูลส่วนบุคคลของคุณ รวมทั้งสิทธิของเจ้าของข้อมูลตามกฎหมายไทย</p>
    </div>

    <section className="space-y-2">
      <h4 className="font-semibold">1) ผู้ควบคุมข้อมูลและช่องทางติดต่อ</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>Kuizy (ผู้ควบคุมข้อมูลส่วนบุคคล)</li>
        <li>อีเมลเจ้าหน้าที่คุ้มครองข้อมูล (DPO): dpo@kuizy.ai</li>
        <li>อีเมลสนับสนุน: support@kuizy.ai</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">2) ขอบเขตและฐานทางกฎหมาย</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>เราดำเนินการภายใต้ PDPA ของไทย และบังคับใช้ทั้งต่อผู้ใช้ในไทยและองค์กรที่ให้บริการแก่บุคคลในไทย</li>
        <li>ฐานทางกฎหมายที่ใช้ประกอบด้วย: ความยินยอม, สัญญา, ภาระหน้าที่ตามกฎหมาย, ประโยชน์โดยชอบด้วยกฎหมาย (ผ่านการชั่งดุลผลประโยชน์), ผลประโยชน์สำคัญต่อชีวิต/ร่างกาย, และภารกิจเพื่อประโยชน์สาธารณะเท่าที่เกี่ยวข้อง</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">3) ประเภทข้อมูลที่เก็บ</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ข้อมูลระบุตัวตนและการติดต่อ: ชื่อ ผู้ใช้ อีเมล โทรศัพท์ รูปโปรไฟล์</li>
        <li>ข้อมูลบัญชีและสิทธิ์: บทบาท สมาชิกคลาส/หลักสูตร การตั้งค่า</li>
        <li>กิจกรรมการเรียนและการสอบ: การลงทะเบียน คำตอบ คะแนน เวลาเข้าออก พฤติกรรมการทำข้อสอบ รายงาน</li>
        <li>เมตาดาต้าอุปกรณ์และบันทึกระบบ: IP ประเภทเบราว์เซอร์ ระบบปฏิบัติการ ภาษา อุปกรณ์ และเหตุการณ์ความปลอดภัย</li>
        <li>ไฟล์/สื่อที่อัปโหลด และการสื่อสารกับทีมสนับสนุน</li>
        <li>ข้อมูลการชำระเงิน (ผ่านผู้ให้บริการรับชำระเงินภายนอก)</li>
        <li>เมื่อเปิดใช้การกำกับดูแลการสอบ (proctoring): ภาพ/เสียงจากกล้องและไมโครโฟน ภาพหน้าจอ เหตุการณ์สลับหน้าต่าง และตัวชี้วัดความน่าเชื่อถือ</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">4) ข้อมูลส่วนบุคคลอ่อนไหว</h4>
      <p>เราไม่มุ่งหมายเก็บข้อมูลอ่อนไหว (เช่น เชื้อชาติ ความเชื่อ ศาสนา สุขภาพ ประวัติอาชญากรรม ชีวภาพ/พันธุกรรม ข้อมูลสหภาพแรงงาน เพศพฤติกรรม ความพิการ ฯลฯ) นอกเสียจากจำเป็นและได้รับความยินยอมชัดแจ้งหรือเข้าเงื่อนไขตามกฎหมาย</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">5) แหล่งข้อมูล</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ได้จากคุณโดยตรงผ่านการสมัคร ใช้งาน หรือส่งคำตอบ</li>
        <li>จากผู้ดูแล/องค์กรที่เชิญคุณเข้าระบบ</li>
        <li>จากคุกกี้และเทคโนโลยีติดตามตามการตั้งค่าความยินยอม</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">6) วัตถุประสงค์ในการประมวลผล</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ให้บริการหลัก: สร้าง/จัดการบัญชี จัดการหลักสูตรและคลาส จัดสอบ ประเมินผล ออกรายงาน/ใบรับรอง</li>
        <li>ปรับปรุงคุณภาพและความปลอดภัย: วิเคราะห์การใช้งาน ป้องกันทุจริต ตรวจจับเหตุผิดปกติ สำรองข้อมูล และทดสอบระบบ</li>
        <li>การสื่อสารและการตลาดที่ได้รับความยินยอม: ข่าวสาร ฟีเจอร์ใหม่ ข้อเสนอ</li>
        <li>การปฏิบัติตามกฎหมายและข้อเรียกร้องทางกฎหมาย</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">7) ระยะเวลาเก็บรักษา</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>เก็บเท่าที่จำเป็นตามวัตถุประสงค์และกฎหมาย เช่น บัญชีผู้ใช้ตลอดอายุการใช้งานและระยะเวลาเพื่อการพิสูจน์สิทธิ์ภายหลัง</li>
        <li>เมื่อพ้นความจำเป็น เราจะลบ ทำให้นิรนาม หรือจำกัดการเข้าถึง</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">8) ผู้รับข้อมูลและการเปิดเผย</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ผู้ประมวลผลข้อมูลที่ให้บริการโฮสติ้ง การวิเคราะห์ ระบบอีเมล การชำระเงิน และเครื่องมือสนับสนุน</li>
        <li>ผู้สอน/องค์กรของคุณในขอบเขตที่จำเป็นต่อการเรียนและการสอบ</li>
        <li>หน่วยงานรัฐตามที่กฎหมายกำหนด</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">9) การโอนข้อมูลไปต่างประเทศ</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ดำเนินการตามกลไกที่กฎหมายรับรอง เช่น ประเทศ/องค์การที่มีมาตรฐานคุ้มครองเพียงพอ ข้อสัญญามาตรฐาน (SCCs) หรือกฎองค์กรภายใน (BCR) ที่ได้รับการรับรอง</li>
        <li>จะใช้มาตรการคุ้มครองที่เหมาะสมและจำกัดการเข้าถึงตามความจำเป็น</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">10) มาตรการความปลอดภัย</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>การเข้ารหัสข้อมูลขณะส่งผ่านและขณะพัก การควบคุมสิทธิ์ การแยกหน้าที่ การบันทึกเหตุการณ์ และการทดสอบความปลอดภัยตามความเสี่ยง</li>
        <li>กระบวนการจัดการเหตุการณ์และการแจ้งเหตุละเมิดข้อมูลแก่หน่วยงานที่เกี่ยวข้องภายในกรอบเวลาตามกฎหมาย</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">11) สิทธิของเจ้าของข้อมูล</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>เข้าถึง ขอสำเนา โอนย้าย แก้ไข ลบ ระงับคัดค้านการประมวลผล และถอนความยินยอม</li>
        <li>เราจะตอบคำขอโดยเร็วและไม่เกิน 30 วัน นับแต่วันที่ได้รับคำขอ เว้นแต่มีเหตุจำเป็นตามกฎหมาย</li>
        <li>ช่องทางส่งคำขอ: dpo@kuizy.ai</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">12) คุกกี้และตัวติดตาม</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>คุกกี้จำเป็นเพื่อการทำงานของระบบใช้โดยไม่ต้องขอความยินยอม</li>
        <li>คุกกี้วิเคราะห์/การตลาดใช้เมื่อคุณให้ความยินยอมและสามารถเปลี่ยนได้ในหน้าตั้งค่าคุกกี้</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">13) การตัดสินใจอัตโนมัติและการทำโปรไฟล์</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ระบบอาจประมวลผลคะแนนหรือสัญญาณผิดปกติอัตโนมัติในบางฟังก์ชัน</li>
        <li>คุณสามารถร้องขอการทบทวนโดยมนุษย์สำหรับการตัดสินใจที่มีผลกระทบมีนัยสำคัญ</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">14) ผู้เยาว์</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>หากผู้ใช้ยังไม่บรรลุนิติภาวะตามกฎหมายไทย การขอความยินยอมจะปฏิบัติตามเกณฑ์ของผู้เยาว์ (ต่ำกว่า 10 ปี ต้องขอจากผู้มีอำนาจปกครอง และช่วง 10–ต่ำกว่า 20 ปี มีเงื่อนไขตามที่กฎหมายกำหนด)</li>
        <li>อาจมีขั้นตอนยืนยันอายุ/ผู้ปกครองตามความเหมาะสม</li>
      </ul>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">15) การเปลี่ยนแปลงนโยบาย</h4>
      <p>หากมีการปรับปรุงนโยบาย เราจะแจ้งผ่านแพลตฟอร์มและระบุวันที่มีผลบังคับใช้ การใช้งานต่อถือว่าเห็นชอบ</p>
    </section>

    <section className="space-y-2">
      <h4 className="font-semibold">16) แบบคำยินยอม</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>ฉันยินยอมให้ Kuizy เก็บ ใช้ และเปิดเผยข้อมูลส่วนบุคคลของฉันเพื่อวัตถุประสงค์ตามที่ระบุไว้</li>
        <li>ฉันยินยอมรับข่าวสารทางอีเมลและทราบว่าสามารถถอนความยินยอมได้ทุกเมื่อผ่านลิงก์ยกเลิกหรือการตั้งค่า</li>
        <li>หากเปิดใช้ proctoring ฉันยินยอมให้มีการเข้าถึงกล้อง/ไมโครโฟนหรือข้อมูลอุปกรณ์เพื่อความซื่อสัตย์ของการสอบตามที่มีการแจ้งในแต่ละครั้ง</li>
      </ul>
    </section>

    <div className="text-xs text-gray-500">คำชี้แจง: เอกสารนี้มีจุดมุ่งหมายเพื่อข้อมูลทั่วไป ไม่ใช่คำปรึกษากฎหมาย องค์กรอาจต้องปรับให้เหมาะกับกระบวนการจริง สัญญา และข้อบังคับเฉพาะ</div>
  </div>
)

export function PdpaModal({ open, onClose, content }) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[999]">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-2 text-gray-900 font-semibold"><Shield className="h-5 w-5" /> นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA)</div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{content || pdpaContent}</div>
              <div className="px-6 py-4 border-t flex justify-end">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-900 text-white">ปิด</button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export function PdpaCheckbox({ checked, onChange, content }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <label className="flex items-start gap-3 text-sm text-gray-700">
        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300" checked={checked} onChange={e => onChange?.(e.target.checked)} />
        <span>ฉันได้อ่านและยินยอมตาม <button type="button" className="underline text-primary" onClick={() => setOpen(true)}>นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA)</button> ของ Kuizy</span>
      </label>
      <PdpaModal open={open} onClose={() => setOpen(false)} content={content} />
    </>
  )
}

export default PdpaCheckbox
